import axios, { type AxiosAdapter, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { readCsrfMetaToken } from './csrf'

export interface PublicClientOptions {
  /** Browser-facing origin (no trailing slash). Default '' = same-origin relative URLs. */
  origin?: string
  /** CSRF token source; defaults to reading Rails' <meta name="csrf-token">. Injectable test seam. */
  csrfToken?: () => string | null
  /** Test seam: override the axios adapter so the HTTP layer runs without a network. */
  adapter?: AxiosAdapter
}

// Anonymous /api/v1/public request/response shapes — hand-written to mirror
// swagger/v1/swagger.yaml. The on-master @twg/api-client has no openapi / @twg/api-schemas
// dependency yet; migrate these to generated types when that package lands on master.
export interface RegistrationPayload {
  universe: string
  role: string
  email: string
  password?: string
  first_name?: string
  last_name?: string
  gender?: string
  organisation_name?: string
  // geo_* / address fields pass straight through to the controller PERMITTED_KEYS.
  [key: string]: unknown
}

export interface RegistrationResponse {
  id: number
  _type: string
  email: string
  userId?: number
  firstName?: string | null
  role: string
  universe: string
  redirectUrl?: string | null
}

export interface AvailabilityParams {
  role: string
  geo_latitude: number
  geo_longitude: number
  category?: string
}

export interface AvailabilityResponse {
  count: number
}

export interface AccountExistsResponse {
  exists: boolean
}

export interface PublicApiClient {
  createRegistration(payload: RegistrationPayload): Promise<RegistrationResponse>
  availability(params: AvailabilityParams): Promise<AvailabilityResponse>
  accountExists(email: string): Promise<AccountExistsResponse>
  /** Session-native POST (same-origin + CSRF) for the OAuth prime — NOT an /api/v1 path. */
  postSession(path: string, body?: unknown): Promise<unknown>
  /** The underlying axios instance (escape hatch). */
  http: AxiosInstance
}

/**
 * Anonymous, same-origin client for the sign-up chatbot's pre-account lane:
 * POST /api/v1/public/registration, GET /availability, GET /account/exists, plus the
 * session-native OAuth prime (SignupFlowsController#prepare_oauth). A pre-account visitor
 * has no token, so — unlike createApiClient — this attaches NO Bearer, does no refresh, and
 * touches no storage. It sends same-origin cookies (`withCredentials`) so the account's
 * `sign_in` Set-Cookie sticks, and every request carries the page's X-CSRF-Token: the lane
 * keeps Rails forgery protection on because a successful registration establishes a
 * session (a cross-site POST would otherwise log the visitor into an attacker's account).
 * See ADR 0025 (public-lane amendment) and ADR 0044.
 */
export function createPublicClient(options: PublicClientOptions = {}): PublicApiClient {
  const { origin = '', csrfToken = readCsrfMetaToken, adapter } = options
  const http = axios.create({ baseURL: origin, adapter, withCredentials: true })

  http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    // Read at request time, not at build time: Rails can swap the meta tag under Turbo navigation.
    const token = csrfToken()
    if (token) config.headers.set('X-CSRF-Token', token)
    return config
  })

  return {
    http,

    createRegistration: (payload) =>
      http
        .post('/api/v1/public/registration', { registration: payload })
        .then((response) => response.data as RegistrationResponse),

    availability: (params) =>
      http
        .get('/api/v1/public/availability', { params })
        .then((response) => response.data as AvailabilityResponse),

    accountExists: (email) =>
      http
        .get('/api/v1/public/account/exists', { params: { email } })
        .then((response) => response.data as AccountExistsResponse),

    postSession: (path, body) => http.post(path, body).then((response) => response.data),
  }
}
